import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import config from "../../../../config";
import { fetchClusterInfo } from "layouts/cluster/data/fetchClusterInfo";
import { useRegionView } from "services/useRegionView";
import { useMaterialUIController } from "context";

const token = config.MAPBOX_ACCESS_TOKEN;

const MapSelect = ({ selectedPoint, setSelectedPoint }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(new mapboxgl.Popup({ closeButton: true }));
  const popupHoverRef = useRef(null);
  const hoverMoveHandlerRef = useRef(null);
  const hoverLeaveHandlerRef = useRef(null);

  const predefinedPoints = [
    { id: 1, coordinates: [28.081913, 36.402218], name: "Cluster 1" },
    { id: 2, coordinates: [28.091913, 36.412218], name: "Cluster 2" },
    { id: 3, coordinates: [28.071913, 36.392218], name: "Cluster 3" },
    { id: 4, coordinates: [28.101913, 36.382218], name: "Cluster 4" },
  ];

  const { center, zoom } = useRegionView();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const mapStyle = darkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/streets-v12";

  // Keep a ref so the init effect can read the current style without re-running
  const mapStyleRef = useRef(mapStyle);
  mapStyleRef.current = mapStyle;

  const setupClusterLayers = async (map) => {
    // Remove any previously registered layer-specific handlers to avoid duplicates
    if (hoverMoveHandlerRef.current) {
      map.off("mousemove", "clusters-layer", hoverMoveHandlerRef.current);
    }
    if (hoverLeaveHandlerRef.current) {
      map.off("mouseleave", "clusters-layer", hoverLeaveHandlerRef.current);
    }
    if (popupHoverRef.current) {
      popupHoverRef.current.remove();
    }

    const clusterData = await fetchClusterInfo();

    map.getCanvas().style.cursor = "pointer";

    if (!map.getSource("clusters")) {
      map.addSource("clusters", { type: "geojson", data: clusterData });
    }
    if (!map.getLayer("clusters-layer")) {
      map.addLayer({
        id: "clusters-layer",
        type: "fill",
        source: "clusters",
        paint: { "fill-color": "#ff6600", "fill-opacity": 0.5 },
      });
    }
    if (!map.getLayer("clusters-outline")) {
      map.addLayer({
        id: "clusters-outline",
        type: "line",
        source: "clusters",
        paint: { "line-color": "#ff0000", "line-width": 2 },
      });
    }

    popupHoverRef.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });

    hoverMoveHandlerRef.current = (e) => {
      const props = e.features[0].properties;
      popupHoverRef.current
        .setLngLat(e.lngLat)
        .setHTML(
          `<strong>${props.name}</strong><br/>
           Cluster ID: ${props.cluster_id}<br/>
           Accidents: ${props.accidents}<br/>
           Indications: ${props.indications}<br/>
           Interventions: ${props.interventions}<br/>
           Last Updated: ${props.latestUpdate}`
        )
        .addTo(map);
    };
    hoverLeaveHandlerRef.current = () => {
      map.getCanvas().style.cursor = "";
      popupHoverRef.current.remove();
    };

    map.on("mousemove", "clusters-layer", hoverMoveHandlerRef.current);
    map.on("mouseleave", "clusters-layer", hoverLeaveHandlerRef.current);
  };

  // Initialize map once
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyleRef.current,
        center,
        zoom,
        accessToken: token,
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl());

      const currentGeolocation = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
        showUserHeading: false,
        showAccuracyCircle: false,
      });
      mapRef.current.addControl(currentGeolocation);
      currentGeolocation.on("geolocate", (e) => {
        const { longitude, latitude } = e.coords;
        mapRef.current.flyTo({ center: [longitude, latitude], zoom: 15, essential: true });
      });

      mapRef.current.on("load", async () => {
        await setupClusterLayers(mapRef.current);

        // General click handler — registered once, persists across style changes
        mapRef.current.on("click", (e) => {
          const { lng, lat } = e.lngLat;
          setSelectedPoint([lng, lat]);
          popupRef.current
            .setLngLat([lng, lat])
            .setHTML(
              `<h3>Επιλεγμένο Σημείο</h3><p>Longitude: ${lng}<br />Latitude: ${lat}</p>`
            )
            .addTo(mapRef.current)
            .on("close", () => setSelectedPoint(null));

          mapRef.current.flyTo({ center: [lng, lat], essential: true, zoom: 12 });
        });
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Switch map style when the site theme changes
  useEffect(() => {
    const map = mapRef.current;
    // Skip on initial mount (map style not yet loaded) — the init effect already sets the correct style
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(mapStyle);
    map.once("style.load", () => setupClusterLayers(map));
  }, [darkMode]);

  // Fly to new region when region selection changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    popupRef.current.remove();
    map.flyTo({ center, zoom, essential: true });
  }, [center, zoom]);

  // Show popup for a pre-programmed point when selectedPoint changes
  useEffect(() => {
    if (selectedPoint && mapRef.current && mapRef.current.isStyleLoaded()) {
      const [lng, lat] = selectedPoint;
      const selectedPointData = predefinedPoints.find(
        (point) => point.coordinates[0] === lng && point.coordinates[1] === lat
      );

      if (selectedPointData) {
        popupRef.current
          .setLngLat([lng, lat])
          .setHTML(
            `<h3>${selectedPointData.name}</h3><p>Longitude: ${lng.toFixed(6)}<br />Latitude: ${lat.toFixed(6)}</p>`
          )
          .addTo(mapRef.current);

        mapRef.current.flyTo({ center: [lng, lat], essential: true, zoom: 12 });
      }
    }
  }, [selectedPoint]);

  return (
    <div
      ref={mapContainerRef}
      style={{ width: "100%", height: "100%" }}
    />
  );
};

export default MapSelect;
